import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import type { Order } from "../types";
import {
  MOTIVI,
  annullaRientro,
  leggiRientri,
  pezzi,
  segnalaRientro,
  type MotivoRientro,
  type StatoRientri,
} from "../rientroMagazzinoApi";
import { ACTION_ICON_CLS, ActionButton } from "./ActionButton";
import { BTN } from "./detail/stile";
import { IconCheck, IconRientro } from "./icons";

/** Il tasto c'e' solo dove la merce e' partita: senza LDV non c'e' niente che torni. */
export function puoRientrare(order: Order): boolean {
  return Boolean(order.trackingCode || order.ldvCode);
}

/**
 * «Rimetti in magazzino» (Davide 2026-11-03): un pezzo tornato indietro va a
 * scaffale come BS COM. Il clic non tocca la giacenza (e' dell'app Logistics):
 * mette il pezzo nella lista che il magazzino legge e carica.
 *
 * ``tile`` sulla card, ``pagina`` nel piede «Sostituzioni e resi» della pagina
 * ordine. Il popup e' lo stesso: e' la conferma, dice cosa parte e perche'.
 */
export function RientroMagazzinoButton({
  order,
  variant = "tile",
}: {
  order: Order;
  variant?: "tile" | "pagina";
}) {
  const [aperto, setAperto] = useState(false);
  const apri = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAperto(true);
  };
  return (
    <>
      {variant === "tile" ? (
        <ActionButton
          tone="teal"
          icon={<IconRientro className={ACTION_ICON_CLS} />}
          label="Rientro"
          ariaLabel={`Rimetti in magazzino ordine ${order.id}`}
          title="Rimetti in magazzino i pezzi tornati indietro"
          onClick={apri}
        />
      ) : (
        <button type="button" onClick={apri} className={BTN}>
          Rimetti in magazzino
        </button>
      )}
      {aperto && <RientroDialog orderRef={order.id} onClose={() => setAperto(false)} />}
    </>
  );
}

export function RientroDialog({ orderRef, onClose }: { orderRef: string; onClose: () => void }) {
  const [stato, setStato] = useState<StatoRientri | null>(null);
  const [motivo, setMotivo] = useState<MotivoRientro>("reso");
  const [scelte, setScelte] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);

  const carica = useCallback(
    (vivo: () => boolean = () => true) =>
      leggiRientri(orderRef)
        .then((s) => {
          if (!vivo()) return;
          setStato(s);
          // Parte da tutto ciò che non è già al magazzino: di solito torna l'ordine intero.
          setScelte(
            Object.fromEntries(
              s.righe.map((r) => [r.sku, r.segnalati > 0 ? 0 : r.quantita] as const),
            ),
          );
        })
        .catch((err: unknown) => vivo() && setErrore(prettifyApiError(err))),
    [orderRef],
  );

  useEffect(() => {
    let vivo = true;
    void carica(() => vivo);
    return () => {
      vivo = false;
    };
  }, [carica]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const righeScelte = Object.fromEntries(Object.entries(scelte).filter(([, q]) => q > 0));
  const totale = Object.values(righeScelte).reduce((a, b) => a + b, 0);
  const attivi = stato?.rientri.filter((r) => r.stato === "da_riporre") ?? [];

  async function conferma() {
    setBusy(true);
    setErrore(null);
    try {
      const esito = await segnalaRientro(orderRef, motivo, righeScelte);
      const n = esito.creati.reduce((a, r) => a + r.quantita, 0);
      setFatto(
        n > 0
          ? `Fatto: ${pezzi(n)} nella lista del magazzino.`
          : "Era già tutto nella lista del magazzino.",
      );
      await carica();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function annulla(id: number) {
    setBusy(true);
    setErrore(null);
    setFatto(null);
    try {
      await annullaRientro(id);
      await carica();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4 max-md:items-end max-md:p-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rientro-titolo"
      data-no-open
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-lg border border-line bg-card text-ink shadow-popover max-md:max-h-[92dvh] max-md:rounded-b-none">
        <header className="border-b border-line px-5 pb-4 pt-5">
          <h3 id="rientro-titolo" className="text-[18px] font-bold leading-tight">
            Rimetti in magazzino
          </h3>
          <p className="mt-1.5 text-[14px] leading-snug">
            Ordine <strong className="font-mono">{orderRef}</strong>. I pezzi vanno nella lista del
            magazzino, che li carica come <strong>BS COM</strong>.
          </p>
        </header>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4 text-[14px]">
          {!stato && !errore && (
            <div aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
              <span className="sr-only">Leggo le righe dell'ordine…</span>
              <div className="h-11 rounded-md bg-paper-2 motion-safe:animate-pulse" />
              <div className="h-11 rounded-md bg-paper-2 motion-safe:animate-pulse" />
            </div>
          )}

          {stato && (
            <>
              <fieldset>
                <legend className="mb-1.5 text-[14px] font-semibold">Perché torna</legend>
                <div className="flex flex-wrap gap-2">
                  {MOTIVI.map((m) => (
                    <label
                      key={m.id}
                      className={`inline-flex min-h-[40px] cursor-pointer items-center rounded-md border px-3 text-[13px] font-semibold transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-teal/50 ${
                        motivo === m.id
                          ? "border-[#00695c] bg-[#00695c] text-white"
                          : "border-line bg-card text-ink hover:border-ink-4"
                      }`}
                    >
                      <input
                        type="radio"
                        name="motivo-rientro"
                        value={m.id}
                        checked={motivo === m.id}
                        onChange={() => setMotivo(m.id)}
                        className="sr-only"
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <section aria-labelledby="rientro-righe">
                <h4 id="rientro-righe" className="mb-1.5 text-[14px] font-semibold">
                  Cosa torna
                </h4>
                {stato.righe.length === 0 ? (
                  <p>Quest'ordine non ha righe con uno SKU: non c'è niente da rimettere.</p>
                ) : (
                  <ul className="divide-y divide-line rounded-md border border-line">
                    {stato.righe.map((r) => (
                      <Riga
                        key={r.sku}
                        sku={r.sku}
                        titolo={r.titolo}
                        massimo={r.quantita}
                        segnalati={r.segnalati}
                        quantita={scelte[r.sku] ?? 0}
                        disabled={busy}
                        onChange={(q) => setScelte((s) => ({ ...s, [r.sku]: q }))}
                      />
                    ))}
                  </ul>
                )}
              </section>

              {attivi.length > 0 && (
                <section aria-labelledby="rientro-attivi">
                  <h4 id="rientro-attivi" className="mb-1.5 text-[14px] font-semibold">
                    Già nella lista del magazzino
                  </h4>
                  <ul className="divide-y divide-line rounded-md border border-teal/40 bg-teal-soft/50">
                    {attivi.map((r) => (
                      <li key={r.id} className="flex items-center gap-3 px-3 py-2">
                        <span className="min-w-0 flex-1">
                          <span className="break-all font-mono text-[13px]">{r.sku}</span>
                          <span className="block text-[12px]">
                            {pezzi(r.quantita)} ·{" "}
                            {MOTIVI.find((m) => m.id === r.motivo)?.label ?? r.motivo} ·{" "}
                            {new Date(r.creato_il).toLocaleDateString("it-IT")}
                            {r.creato_da ? ` · ${r.creato_da}` : ""}
                          </span>
                        </span>
                        <button
                          type="button"
                          disabled={busy}
                          aria-label={`Togli ${r.sku} dalla lista del magazzino`}
                          onClick={() => void annulla(r.id)}
                          className="min-h-[36px] shrink-0 rounded-md px-2 text-[13px] font-semibold text-red underline-offset-2 hover:underline disabled:opacity-50"
                        >
                          Togli
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {fatto && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-md border border-green/30 bg-green-soft px-3 py-2"
            >
              <IconCheck className="h-4 w-4 shrink-0 stroke-current fill-none stroke-2" />
              {fatto}
            </p>
          )}
          {errore && (
            <p role="alert" className="rounded-md border border-red/30 bg-red-soft px-3 py-2">
              {errore}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3 max-md:pb-[calc(env(safe-area-inset-bottom)+12px)]">
          <button type="button" onClick={onClose} disabled={busy} className={BTN}>
            Chiudi
          </button>
          <button
            type="button"
            onClick={() => void conferma()}
            disabled={busy || totale === 0 || !stato}
            className="inline-flex min-h-[38px] items-center rounded-md border border-[#00695c] bg-[#00695c] px-4 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#00564b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy
              ? "Mando…"
              : totale > 0
                ? `Manda al magazzino · ${pezzi(totale)}`
                : "Manda al magazzino"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Riga({
  sku,
  titolo,
  massimo,
  segnalati,
  quantita,
  disabled,
  onChange,
}: {
  sku: string;
  titolo: string;
  massimo: number;
  segnalati: number;
  quantita: number;
  disabled: boolean;
  onChange: (q: number) => void;
}) {
  // Uno SKU già in lista si annulla e si rifà: due righe attive vorrebbero dire
  // due carichi dello stesso pezzo.
  if (segnalati > 0) {
    return (
      <li className="flex items-center gap-3 bg-paper px-3 py-2">
        <span className="min-w-0 flex-1">
          <span className="break-all font-mono text-[13px]">{sku}</span>
          {titolo && <span className="block truncate text-[12px]">{titolo}</span>}
        </span>
        <span className="shrink-0 text-[12px] font-semibold">Già in lista</span>
      </li>
    );
  }
  const inclusa = quantita > 0;
  return (
    <li className={`flex items-center gap-3 px-3 py-1 ${inclusa ? "" : "bg-paper"}`}>
      <label className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={inclusa}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? massimo : 0)}
          className="h-[18px] w-[18px] shrink-0 cursor-pointer accent-teal"
        />
        <span className="min-w-0">
          <span className={`break-all font-mono text-[13px] ${inclusa ? "" : "line-through"}`}>
            {sku}
          </span>
          {titolo && <span className="block truncate text-[12px]">{titolo}</span>}
        </span>
      </label>
      {massimo > 1 ? (
        <div
          role="group"
          aria-label={`Pezzi di ${sku}`}
          className="flex shrink-0 items-center gap-1 font-mono tabular-nums"
        >
          <Passo
            label={`Un ${sku} in meno`}
            disabled={disabled || !inclusa || quantita <= 1}
            onClick={() => onChange(quantita - 1)}
          >
            −
          </Passo>
          <output aria-live="polite" className="min-w-[52px] px-1 text-center text-[14px]">
            {quantita} di {massimo}
          </output>
          <Passo
            label={`Un ${sku} in più`}
            disabled={disabled || quantita >= massimo}
            onClick={() => onChange(quantita + 1)}
          >
            +
          </Passo>
        </div>
      ) : (
        <span className="shrink-0 font-mono tabular-nums">× 1</span>
      )}
    </li>
  );
}

function Passo({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-md border border-line bg-card text-[16px] font-semibold hover:border-ink-4 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
